import React, { FunctionComponent } from "react";
import { MedigapPlan, MedigapPlanType } from "../utils/plans";
import { useTranslate } from "../../../helpers";
import PlanDetailsTable from "./PlanDetailsTable";
import ExclamationNote from "./ExclamationNote";

interface ExtraBenefitsProps {
  planData: MedigapPlan;
  planType: MedigapPlanType;
}

const ExtraBenefits: FunctionComponent<ExtraBenefitsProps> = ({
  planData,
  planType,
}) => {
  const t = useTranslate();

  const title = t("medigap.plan_details.extra_benefits");
  const data = [];

  const {
    foreign_travel_emergency_first,
    foreign_travel_emergency_remainder_then,
    foreign_travel_emergency,
    state,
    state_mand,
  } = planData;

  const showCoverageWhileInForeignCountryText = [
    MedigapPlanType.MN_EXT_BASIC,
    MedigapPlanType.MN_EXT_BASIC_NEW,
  ].includes(planType);

  if (foreign_travel_emergency) {
    data.push({
      property: showCoverageWhileInForeignCountryText
        ? t("medigap.plan_details.coverage_foreign_country")
        : t("medigap.plan_details.foreign_travel"),
      value: t(foreign_travel_emergency),
    });
  }

  if (
    foreign_travel_emergency_first &&
    foreign_travel_emergency_remainder_then
  ) {
    data.push({
      property: t("medigap.plan_details.foreign_travel"),
      value: [
        {
          property: t("medigap.plan_details.first"),
          value: t(foreign_travel_emergency_first),
        },
        {
          property: t("medigap.plan_details.then"),
          value: t(foreign_travel_emergency_remainder_then),
        },
      ],
    });
  }

  if ((state === "WI" || state === "MN") && state_mand) {
    data.push({
      property: t("medigap.state_mandated_benefits"),
      tooltip:
        state === "WI"
          ? t("medigap.state_mandated_benefits_wi")
          : t("medigap.state_mandated_benefits_mn"),
      value: t(state_mand),
    });
  }

  const footerNotes: { text: string | JSX.Element; id: string }[] = [
    {
      id: "mn-basic",
      text:
        planType === MedigapPlanType.MN_BASIC ? (
          <ExclamationNote
            message={t("medigap.extra_benefits.mn_basic_footnote")}
          />
        ) : (
          ""
        ),
    },
    {
      id: "wi-basic",
      text:
        planType === MedigapPlanType.WI_BASIC ? (
          <ExclamationNote
            message={t("medigap.extra_benefits.wi_basic_footnote")}
          />
        ) : (
          ""
        ),
    },
    {
      id: "wi-25-50",
      text: [MedigapPlanType.WI_25_COST, MedigapPlanType.WI_50_COST].includes(
        planType
      ) ? (
        <ExclamationNote
          message={t("medigap.extra_benefits.wi_25_50_footnote")}
        />
      ) : (
        ""
      ),
    },
    {
      id: "contact-company",
      text: t("medigap.extra_benefits.contact_company_footnote"),
    },
  ];

  return <PlanDetailsTable table={{ title, data, footerNotes }} />;
};

export default ExtraBenefits;
